import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth, API_ENDPOINTS } from '../config/api';

export default function PaymentSuccessPage() {
  // console.log('PaymentSuccessPage: Component mounted');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser: user } = useAuth();
  
  // console.log('PaymentSuccessPage: Initial state', {
  //   loading,
  //   error,
  //   'session_id': searchParams.get('session_id'),
  //   'user': user?.uid
  // });

  useEffect(() => {
    // console.log('PaymentSuccessPage: useEffect triggered', {
    //   'user': user?.uid,
    //   'searchParams': Object.fromEntries(searchParams.entries())
    // });

    const verifyPayment = async () => {
      try {
        // console.log('PaymentSuccessPage: Starting payment verification');
        const sessionId = searchParams.get('session_id');
        // console.log('PaymentSuccessPage: Session ID:', sessionId);
        
        if (!sessionId) {
          setError('No session ID found');
          setLoading(false);
          return;
        }

        // Verify the payment was successful
        try {
          const apiUrl = API_ENDPOINTS.VERIFY_PAYMENT + `?session_id=${sessionId}`;
          // console.log('PaymentSuccessPage: Making API request to:', apiUrl);
          // console.log('PaymentSuccessPage: Full URL:', `${process.env.REACT_APP_API_URL}${apiUrl}`);
          
          const response = await fetchWithAuth(apiUrl);
          // console.log('PaymentSuccessPage: API response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('PaymentSuccessPage: API error response:', errorText);
            throw new Error(`API request failed: ${errorText}`);
          }
          
          const data = await response.json();
          // console.log('PaymentSuccessPage: API response data:', data);
          
          if (!data || !data.success) {
            throw new Error(data.error || 'Payment verification failed');
          }

          // Payment verified successfully, navigate to home after a brief delay
          setTimeout(() => {
            setLoading(false);
            navigate('/');
          }, 2000);
        } catch (apiError) {
          console.error('PaymentSuccessPage: API call error:', apiError);
          throw apiError;
        }
      } catch (err) {
        console.error('PaymentSuccessPage: Error during verification:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    if (!user) {
      // console.log('PaymentSuccessPage: No user found');
      setError('Please log in to verify your payment');
      setLoading(false);
      return;
    }
    // console.log('PaymentSuccessPage: User authenticated, proceeding with verification');

    verifyPayment();
  }, [user, searchParams, navigate]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={3}
      >
        <CircularProgress />
        <Typography variant="h6">Verifying your payment...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={3}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/pricing')}>
          Return to Pricing
        </Button>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={3}
    >
      <Typography variant="h4" color="primary">
        Payment Successful!
      </Typography>
      <Typography variant="body1">
        Thank you for your purchase. Your credits have been added to your account.
      </Typography>
      <Button variant="contained" onClick={() => navigate('/documents')}>
        Go to Documents
      </Button>
    </Box>
  );
}
